import IExtension from "./IExtension";
import { Naja } from "naja";

/**
 * Prevents duplicate AJAX requests caused by rapid clicking on ajax links/buttons
 * before the first request completes.
 *
 * 1. Per-element protection: clicking the same element twice is blocked.
 * 2. Group protection (data-ajax-group="<name>"): when one element in a group is
 *    clicked, all OTHER elements in the same group are disabled (without showing
 *    a spinner) until the request completes. This prevents e.g. clicking "Tina analyzes"
 *    on multiple comments simultaneously.
 */
export default class PreventDoubleClickExtension implements IExtension {

    private static readonly PENDING_CLASS = 'ajax-pending';
    private static readonly GROUP_DISABLED_CLASS = 'ajax-group-disabled';

    private readonly pendingElements: Set<HTMLElement> = new Set();
    private readonly groupDisabledElements: Set<HTMLElement> = new Set();

    initialize(naja: Naja): void {
        // Block click events on group-disabled elements in the capture phase,
        // before ShowLoaderExtension click handlers can show spinners.
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            const groupElement = target.closest(`.${PreventDoubleClickExtension.GROUP_DISABLED_CLASS}, .${PreventDoubleClickExtension.PENDING_CLASS}`) as HTMLElement | null;
            if (groupElement && (this.groupDisabledElements.has(groupElement) || this.pendingElements.has(groupElement))) {
                e.stopImmediatePropagation();
                e.preventDefault();
            }
        }, true);

        naja.uiHandler.addEventListener('interaction', (e) => {
            if (e.defaultPrevented) {
                return;
            }

            const element = e.detail.element as HTMLElement;

            // Block if this exact element is already pending
            if (this.pendingElements.has(element)) {
                e.preventDefault();
                return;
            }

            // Block if this element was disabled by a group lock
            if (this.groupDisabledElements.has(element)) {
                e.preventDefault();
                return;
            }

            // Skip double-click prevention if element has data-ignore-doubleclick-prevention
            if (element.hasAttribute('data-ignore-doubleclick-prevention')) {
                return;
            }

            this.pendingElements.add(element);
            element.classList.add(PreventDoubleClickExtension.PENDING_CLASS);

            // Disable all other elements in the same group
            const group = element.dataset.ajaxGroup;
            if (group) {
                document.querySelectorAll<HTMLElement>(`[data-ajax-group="${group}"]`).forEach((el) => {
                    if (el !== element && !this.pendingElements.has(el)) {
                        this.groupDisabledElements.add(el);
                        el.classList.add(PreventDoubleClickExtension.GROUP_DISABLED_CLASS);
                    }
                });
            }
        });

        naja.addEventListener('complete', () => {
            this.pendingElements.forEach((element) => {
                element.classList.remove(PreventDoubleClickExtension.PENDING_CLASS);
            });
            this.pendingElements.clear();

            this.groupDisabledElements.forEach((element) => {
                element.classList.remove(PreventDoubleClickExtension.GROUP_DISABLED_CLASS);
            });
            this.groupDisabledElements.clear();
        });
    }
}

